import React, { useMemo } from "react";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Edit from "@mui/icons-material/Edit";
import Add from "@mui/icons-material/Add";
import { useGame, useGamePublishers, useMyGameEditions } from "@seabrookstudios/pitch2table";
import { formatFullDate } from "../core/date-formatting";
import AddGameEditionFullScreenDialog from "./AddGameEditionFullScreenDialog";
import EditGameEditionFullScreenDialog from "./EditGameEditionFullScreenDialog";
import ListItemButton from "@mui/material/ListItemButton";
import Refresh from "@mui/icons-material/Refresh";
import Stack from "@mui/material/Stack";
import { CircularProgressSize } from "../theme";
import CircularProgress from "@mui/material/CircularProgress";
import { CoverSquare } from "./CoverImages";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const GameEditionsCard = ({ gameId }) => {
  const { data: publishers = [] } = useGamePublishers(gameId);
  const { data: editions = [], refetch, isRefetching } = useMyGameEditions(gameId);
  const { data: game } = useGame(gameId);

  const publisherNameById = useMemo(
    () => new Map(publishers.map((p) => [p.publisherId, p.publisherName])),
    [publishers]
  );

  // Sort editions alphabetically by name
  const sortedEditions = useMemo(() => {
    return [...editions].sort((a, b) => a.name.localeCompare(b.name));
  }, [editions]);

  return (
    <Card elevation={1} square={false}>
      <List>
        <ListItem>
          <ListItemText primary="Releases" />
          <ListItemSecondaryAction>
            <Stack direction="row" spacing={1}>
              <IconButton color="primary" size="small" onClick={refetch}>
                {isRefetching ? <CircularProgress size={CircularProgressSize} /> : <Refresh />}
              </IconButton>
              <AddGameEditionFullScreenDialog
                gameId={gameId}
                CustomControl={
                  <IconButton color="primary" size="small">
                    <Add />
                  </IconButton>
                }
              />
            </Stack>
          </ListItemSecondaryAction>
        </ListItem>
        {sortedEditions.map((edition) => {
          const parts = [];
          if (edition.datePublished !== null && edition.datePublished !== undefined && edition.datePublished !== 0) {
            parts.push(formatFullDate(edition.datePublished));
          }
          if (edition.publisherId) {
            const name = publisherNameById.get(edition.publisherId);
            if (name) parts.push(name);
          }

          const coverGame = game && edition.image ? { ...game, image: edition.image } : undefined;

          return (
            <ListItem key={edition.id}>
              {coverGame && (
                <ListItemAvatar>
                  <CoverSquare game={coverGame} width={48} height={48} />
                </ListItemAvatar>
              )}
              <ListItemText primary={edition.name} secondary={parts.join(" • ")} />
              <EditGameEditionFullScreenDialog
                gameId={gameId}
                edition={edition}
                CustomControl={
                  <ListItemButton>
                    <Edit color="primary" />
                  </ListItemButton>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );
};
